// SPDX-License-Identifier: AGPL-3.0-or-later

import type {LeaderboardPeriod} from '@app/features/table_game_leaderboard/state/TableGameLeaderboards';
import {
	LEAGUE_PROGRESS_DESCRIPTOR,
	RR_DESCRIPTOR,
} from '@app/features/table_game_leaderboard/utils/TableGameLeaderboardMessages';
import type {I18n} from '@lingui/core';

const PODIUM_PLACES = 3;
// Seasons are Istanbul calendar months; Istanbul has kept UTC+3 without daylight saving since 2016.
const ISTANBUL_OFFSET_MS = 3 * 3_600_000;
const DAY_MS = 86_400_000;

// The head of a board that stands on the podium (places 1 to 3) and the rows listed under it.
export function splitPodium<T extends {readonly rank: number}>(
	rows: ReadonlyArray<T>,
): {podium: ReadonlyArray<T>; rest: ReadonlyArray<T>} {
	let count = 0;
	while (count < PODIUM_PLACES && count < rows.length && rows[count]!.rank <= PODIUM_PLACES) count += 1;
	return {podium: rows.slice(0, count), rest: rows.slice(count)};
}

// The month of the chosen season and, for the current one, the whole days after today until it ends. The board's own
// season (YYYYMM) wins over the client clock, which only fills in before the first page arrives.
export function seasonOf(
	nowMs: number,
	period: LeaderboardPeriod,
	season: number | null = null,
): {year: number; month: number; daysLeft: number | null} {
	const local = new Date(nowMs + ISTANBUL_OFFSET_MS);
	let year = local.getUTCFullYear();
	let month = local.getUTCMonth() + 1;
	if (season !== null) {
		year = Math.floor(season / 100);
		month = season % 100;
	} else if (period === 'previous') {
		[year, month] = month === 1 ? [year - 1, 12] : [year, month - 1];
	}
	if (period === 'previous') return {year, month, daysLeft: null};
	const tomorrowMs = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate() + 1);
	const seasonEndMs = Date.UTC(year, month, 1);
	return {year, month, daysLeft: Math.max(0, Math.round((seasonEndMs - tomorrowMs) / DAY_MS))};
}

export function leagueProgressPercent(progress: number): number {
	return Math.min(100, Math.max(0, Math.floor(progress * 100)));
}

// A player's standing inside their league: RR in the top league, else the progress toward the next one.
export function leagueStanding(i18n: I18n, rr: number | null, progress: number | null): string | null {
	if (rr !== null) return i18n._(RR_DESCRIPTOR, {rr});
	if (progress !== null) return i18n._(LEAGUE_PROGRESS_DESCRIPTOR, {percent: leagueProgressPercent(progress)});
	return null;
}
